/**
 * Eventos de auditoría que la pantalla sabe nombrar (#162).
 *
 * El backend devuelve **todos** los eventos del documento; este catálogo decide cuáles se pueden
 * mostrar y con qué nombre. Cada entrada agrupa **uno o más pares** `(tipo, suceso)` bajo un solo
 * nombre de negocio, porque el mismo hecho se registra de formas distintas según por dónde pasó:
 * un rechazo en aprobación y uno en firma son dos pares, y para el usuario son "Rechazado".
 *
 * Tres trampas que este archivo tiene que respetar, todas verificadas contra el código:
 *
 * 1. **El suceso de aprobación NO es el de publicación.** Se llamaba `PUBLICADO` en el enum de Java
 *    y `APROBADO` en la base; desde #187 los dos dicen `APPROVED`, que es lo que siempre significó:
 *    se emite en toda aprobación, publique o no. La publicación tiene su propio par.
 * 2. **La importación por el bot usa otro tipo**: `TELEGRAM_IMPORT/IMPORTED`. Sin ese par, un
 *    documento cargado por Telegram no mostraría "Importado".
 * 3. **`SIGNATURE_REJECTED` se escribe con estado ERROR**, no OK. Si el filtro exigiera OK a todos,
 *    el rechazo en firma no aparecería nunca.
 *
 * El vocabulario pasó al inglés en #187 (API_GUIDELINES §2.1). Los `id` de la base no cambiaron, así
 * que los eventos ya registrados siguen resolviendo igual; lo que cambia es el nombre que viaja en
 * el JSON. Las preferencias que cada usuario tenía guardadas se migran en `CLAVES_MIGRADAS`.
 */

/** Clave estable de un par. Es lo que se persiste, ver `PARES_POR_DEFECTO`. */
export const clavePar = (tipo, suceso) => `${tipo}/${suceso}`;

const par = (tipo, suceso, status = 'OK') => ({ tipo, suceso, status, clave: clavePar(tipo, suceso) });

/**
 * Lo que la pantalla ofrece, en el orden en que aparece en el selector.
 *
 * `porDefecto` reproduce exactamente lo que la vista mostraba antes de este cambio: quien no toque
 * nada ve lo mismo de siempre.
 */
export const AUDIT_EVENTS = [
  { key: 'consultado', label: 'Consultado', porDefecto: true, pares: [par('ACCESS', 'VIEWED')] },
  { key: 'descargado', label: 'Descargado', porDefecto: true, pares: [par('ACCESS', 'DOWNLOADED')] },
  {
    key: 'importado',
    label: 'Importado',
    pares: [par('IMPORT', 'IMPORTED'), par('TELEGRAM_IMPORT', 'IMPORTED')],
  },
  { key: 'revisado', label: 'Revisado', pares: [par('REVIEW', 'REVIEWED')] },
  { key: 'aprobado', label: 'Aprobado', pares: [par('APPROVAL', 'APPROVED')] },
  {
    key: 'rechazado',
    label: 'Rechazado',
    pares: [par('APPROVAL', 'REJECTED'), par('SIGNATURE', 'SIGNATURE_REJECTED', 'ERROR')],
  },
  { key: 'firmado', label: 'Firmado', pares: [par('SIGNATURE', 'SIGNATURE_COMPLETED')] },
  {
    // Seis pares bajo un nombre (#187). La publicación no es una acción: sale de cruzar la
    // parametrización de la serie con el circuito recorrido, así que hay seis formas de llegar y el
    // usuario quiere ver una sola cosa — "se publicó" — sin perder por cuál camino fue. Es
    // exactamente para lo que ADR-042 hizo que una entrada agrupe N pares.
    key: 'publicado',
    label: 'Publicado',
    pares: [
      par('PUBLICATION', 'PUBLISHED_BY_IMPORT'),
      par('PUBLICATION', 'PUBLISHED_BY_OCR'),
      par('PUBLICATION', 'PUBLISHED_BY_METADATA'),
      par('PUBLICATION', 'PUBLISHED_BY_REVIEW'),
      par('PUBLICATION', 'PUBLISHED_BY_APPROVAL'),
      par('PUBLICATION', 'PUBLISHED_BY_SIGNATURE'),
    ],
  },
];

/**
 * Claves de par viejas → nuevas, para las preferencias que los usuarios ya tenían guardadas (#187).
 *
 * Lo que se persiste en el navegador es el par como texto, así que un renombre del vocabulario deja
 * esas preferencias apuntando a pares que ya no existen. Sin esta tabla, quien tenía "Aprobado"
 * tildado dejaría de verlo **sin ningún aviso** — que es exactamente el modo de falla que el resto
 * de este archivo se ocupa de evitar.
 *
 * Se aplica al cargar, no al guardar: en cuanto el usuario toque el selector, lo que se persiste ya
 * son las claves nuevas. La tabla se puede borrar cuando no queden navegadores con la forma vieja;
 * mientras tanto cuesta un `map`.
 */
export const CLAVES_MIGRADAS = new Map([
  ['AUDITORIA/VISUALIZACION', 'ACCESS/VIEWED'],
  ['AUDITORIA/BAJADA', 'ACCESS/DOWNLOADED'],
  ['IMPORTACION/IMPORTADO', 'IMPORT/IMPORTED'],
  ['IMPORTACION_TELEGRAM/IMPORTADO', 'TELEGRAM_IMPORT/IMPORTED'],
  ['REVIEW/REVISADO', 'REVIEW/REVIEWED'],
  ['APROBACION/PUBLICADO', 'APPROVAL/APPROVED'],
  ['APROBACION/RECHAZADO', 'APPROVAL/REJECTED'],
]);

/**
 * Traduce una lista de pares guardada al vocabulario vigente.
 *
 * Descarta los que no existen ni antes ni ahora: una preferencia corrupta o de una versión que ya
 * no está no puede arrastrar una clave muerta que después no matchea con nada.
 */
export const migrarPares = (pares = []) => {
  const vigentes = new Set([
    ...AUDIT_EVENTS.flatMap((e) => e.pares.map((p) => p.clave)),
    ...PARES_OCULTOS,
  ]);
  const migrados = [...new Set(pares.map((c) => CLAVES_MIGRADAS.get(c) ?? c).filter((c) => vigentes.has(c)))];
  // Una preferencia vacía a propósito es legítima; una que quedó vacía DESPUÉS de filtrar significa
  // que lo guardado no se entiende, y dejar la pantalla con todo destildado y sin explicación es
  // peor que volver al default.
  return migrados.length === 0 && pares.length > 0 ? [...PARES_POR_DEFECTO] : migrados;
};

/**
 * Pares que el backend escribe y esta vista **decide no mostrar**, declarados uno por uno.
 *
 * Declararlos en vez de simplemente omitirlos es lo que convierte el olvido en un rojo de CI: el
 * test cruza visibles + ocultos contra todo lo que el backend emite, así que un evento nuevo obliga
 * a decir "se muestra así" o "no se muestra". Sin eso, un evento no mapeado no rompe nada —
 * desaparece —, y en una traza de auditoría lo que no se ve se interpreta como que no pasó.
 */
export const PARES_OCULTOS = [
  // Los tres pasos intermedios de la importación: se registran junto con IMPORTED y mostrarlos
  // pondría cuatro filas idénticas por documento.
  clavePar('IMPORT', 'REGISTERED'),
  clavePar('IMPORT', 'STORED'),
  clavePar('IMPORT', 'INDEXED'),
  clavePar('TELEGRAM_IMPORT', 'REGISTERED'),
  clavePar('TELEGRAM_IMPORT', 'STORED'),
  clavePar('TELEGRAM_IMPORT', 'INDEXED'),
  // Apertura del documento desde Control de Calidad. El nombre del suceso engaña: no es la
  // revisión, es una consulta (`DocumentService.streamDocForApproval`).
  clavePar('APPROVAL', 'REVIEWED'),
  // OCR procesado. El código lo escribe pero no hay ni una fila en test ni en producción.
  clavePar('OCR', 'INDEXED'),
  // Detalle interno del circuito de firma. `SIGNATURE/VISUALIZACION` se instrumentó a propósito
  // (#119) para responder "quién miró antes de firmar"; se oculta por ahora, no por descuido.
  clavePar('SIGNATURE', 'VIEWED'),
  clavePar('SIGNATURE', 'SIGNATURE_REQUESTED'),
  clavePar('SIGNATURE', 'SIGNATURE_STARTED'),
  clavePar('SIGNATURE', 'SIGNATURE_PRECHECK_FAILED'),
  clavePar('SIGNATURE', 'SIGNATURE_FAILED'),
  clavePar('SIGNATURE', 'SIGNATURE_LOCK_RELEASED_BY_ADMIN'),
  clavePar('SIGNATURE', 'SIGNATURE_SUPERSEDED'),
  clavePar('SIGNATURE', 'SELF_APPROVED_SIGNATURE'),
  clavePar('SIGNATURE', 'SIGNATURE_REVOCATION_UNCHECKED'),
  clavePar('REVIEW', 'METADATA_UPDATE'),
];

/**
 * Índice par → entrada del catálogo, **con el vocabulario anterior a #187 como alias**.
 *
 * No es redundancia: lo que viaja en el JSON es el nombre del enum de Java, no el de la tabla, así
 * que con la UI y la API en versiones distintas —un rollback de una sola imagen, o los segundos del
 * despliegue en que nginx ya sirve el JS nuevo y la API todavía es la vieja— el catálogo no
 * reconocería NINGÚN evento y la auditoría de todos los documentos se vería vacía, con un aviso al
 * pie que se lee como una decisión de producto y no como una versión desalineada.
 *
 * El alias resuelve a la entrada **canónica**: el evento viejo se muestra con el nombre de siempre y
 * queda cubierto por el par nuevo, así que no ensucia ni la selección del usuario ni los defaults.
 * Se borra junto con CLAVES_MIGRADAS cuando no queden imágenes viejas.
 */
const POR_CLAVE = new Map(
  AUDIT_EVENTS.flatMap((e) => e.pares.map((p) => [p.clave, { ...p, label: e.label, key: e.key }])),
);
for (const [vieja, nueva] of CLAVES_MIGRADAS) {
  const canonica = POR_CLAVE.get(nueva);
  if (canonica) POR_CLAVE.set(vieja, canonica);
}

/**
 * Lo que se persiste son **los pares**, no las claves de negocio: el día que "Rechazado" se parta
 * en dos nombres, quien lo tenía elegido sigue viendo los dos rechazos en vez de quedarse con una
 * clave muerta y dejar de ver rechazos sin que nadie le avise.
 */
export const PARES_POR_DEFECTO = AUDIT_EVENTS
  .filter((e) => e.porDefecto)
  .flatMap((e) => e.pares.map((p) => p.clave));

/** Todos los pares que el catálogo sabe nombrar. */
export const PARES_DE = (key) => (AUDIT_EVENTS.find((e) => e.key === key)?.pares || []).map((p) => p.clave);

/** El evento tal como viene del API → la entrada del catálogo, o `null` si no se sabe nombrar. */
export const entradaDe = (evento) => {
  if (!evento?.type || !evento?.event) return null;
  const entrada = POR_CLAVE.get(clavePar(evento.type, evento.event));
  if (!entrada) return null;
  // El estado esperado es parte de la identidad: `riseReviewedDocEvent` recibe el status por
  // parámetro, así que un REVISADO en ERROR no puede mostrarse con el nombre del éxito. Y un
  // estado ausente tampoco se asume OK: si la columna vino NULL, el enum llega en `null` y ese
  // evento está corrupto — mostrarlo como "Revisado" sería afirmar un éxito que nadie registró.
  return entrada.status === evento.status ? entrada : null;
};

/** Los eventos que corresponde mostrar, dados los pares que el usuario tiene elegidos. */
export const eventosVisibles = (eventos = [], paresElegidos = []) => {
  const elegidos = new Set(paresElegidos);
  return eventos.filter((ev) => {
    const entrada = entradaDe(ev);
    return entrada != null && elegidos.has(entrada.clave);
  });
};

